import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table } from "../blocks.js";
import type { WikiTopic } from "../types.js";

export const retiredTypes: WikiTopic = {
  slug: "retired-types",
  title: "Retired and unregistered types",
  summary: "Four types the feed still carries, and one strategy its own measurement rejected.",
  group: "reference",
  sections: [
    {
      heading: "The four types the hourly ICT left behind",
      code: "mss_bullish / mss_bearish / fvg_bullish / fvg_bearish",
      blocks: [
        p(
          <>
            An hourly ICT strategy wrote these four. It was deleted when the swing engine took over
            the same six patterns, because two detectors of one pattern writing to one{" "}
            <Datum>signals</Datum> table make the forward-return data unreadable: a row saying a
            fair value gap formed stops saying whose definition of a gap it means.
          </>,
        ),
        p(
          <>
            The rows it already wrote are still in the table. They still carry{" "}
            <Datum>family: intraday</Datum>, because family is a fact about the job that emitted a
            row rather than about the pattern, and the Type dropdown still names all four under the
            group labelled Retired. A filter that could not reach them would leave rows on the page
            nobody can select.
          </>,
        ),
        note(
          <>
            <Datum>ict_mss_*</Datum> and <Datum>ict_fvg_*</Datum> are twins of these, not renames.
            They come from the session-close job and carry <Datum>family: swing</Datum>, so a filter
            naming one will never match the other.
          </>,
        ),
      ],
    },
    {
      heading: "pivot_trend is built, tested, and not registered",
      code: "pivot_trend_bullish / pivot_trend_bearish",
      blocks: [
        p(
          <>
            The strategy exists in full: a tight or Inside Value central pivot range, a run of
            same-bias two-day CPR relationships, an opening print that confirms the bias, and an
            hourly close past the previous session's edge, with stop and target read off a merged
            daily and weekly ladder. It is absent from the strategy registry and absent from{" "}
            <Datum>STRATEGY_KEYS</Datum>, so it never runs and there is no switch that starts it.
          </>,
        ),
        p(
          <>
            Its own measurement rejected it. Over <Datum>784,110</Datum> classified NIFTY 500
            sessions from <Datum>2018-07-10</Datum> to <Datum>2026-08-28</Datum>, Higher Value beat
            Lower Value by <Datum>0.031%</Datum> at five sessions against a <Datum>0.400%</Datum>{" "}
            round trip. Five sessions is the horizon with the most matured observations, and the
            design fails there by an order of magnitude.
          </>,
        ),
        table(
          "What the pivot measurement found",
          ["Reading", "Result"],
          [
            ["Sessions classified", <Datum key="n">784,110</Datum>],
            ["Higher less Lower, 5 sessions", <Datum key="g">0.031%</Datum>],
            ["Round trip it has to clear", <Datum key="c">0.400%</Datum>],
            ["Conditions selecting the worse side", <Datum key="k">4 of 5</Datum>],
          ],
        ),
        p(
          <>
            Four of the five conditions select populations at or below the unconditional median.
            Narrow ranges lose to wide at every horizon, longer runs lose to shorter, a denied open
            beats a confirmed one, and Camarilla-sourced targets do worse than Standard while
            supplying <Datum>85%</Datum> of them.
          </>,
        ),
        note(
          <>
            What ships from that work is the pivot section on the chart. Levels are derived from
            candles and never stored, so they render for any symbol whether or not a signal fired,
            and the measurement is an argument for showing a trader the levels rather than for
            firing on them.
          </>,
        ),
      ],
    },
    {
      heading: "Why no switch names any of these",
      blocks: [
        p(
          <>
            The settings page hides rows by asking which strategy owns a type.{" "}
            <Datum>strategyKeyOf</Datum> answers undefined for a type no catalogue entry owns, and
            that answer is load-bearing rather than a gap: a type with no owner is never hidden. A
            switch that suppressed rows it does not name would be a switch nobody could reason
            about.
          </>,
        ),
        p(
          <>
            <Datum>tradeHorizonOf</Datum> answers null for the same types, and the column renders
            the null as a dash. A registry that answered session there would be stating a holding
            period no strategy ever claimed.
          </>,
        ),
        note(
          <>
            Listing <Datum>pivot_trend</Datum> in the catalogue would turn a toggle-off feature into
            a toggle-on one. Its two types still sit in the Type dropdown beside{" "}
            <Datum>momentum_pivot</Datum>'s, which is where a reader looking for a pivot strategy
            looks.
          </>,
        ),
      ],
    },
  ],
};
